import { DeviceConfiguration } from "./Configurations/DeviceConfiguration.js";
import { ThirdPartyDeviceType } from "./DeviceTypes/ThirdPartyDevice.js";
import { FirstPartyDeviceType, SensorType } from "./shared.js";
import { ThirdPartyDeviceConfiguration } from "./Configurations/ThirdPartyDeviceConfiguration.js";
import { DeviceTypeConfigurations } from "./Configurations/DeviceTypeConfiguration/DeviceTypeConfigurations.js";
import { SensorConfigurations } from "./Configurations/SensorConfigurations/SensorConfiguration.js";
import { DataTypeSpecifier } from "./HistoricalData.js";
import { SensorConfiguration } from "./Configurations/SensorConfigurations/SensorConfigurationTypes.js";
import { assertValidSensorConfigs } from "./Configurations/SensorConfigurations/SensorConfigurationValidator.js";

export * from "./WarningDescription.js";
export * from "./shared.js";
export * from "./settingsModels.js";
export * from "./DeviceTypes/Device.js";
// Schema exports are now directly exported from their new locations
// Legacy AuxillaryDeviceModels exports moved to schemas/
export * from "./HistoricalData.js";
export * from "./DeviceTypes/ThirdPartyDevice.js";
export * from "./utils/VerdiDeviceVersionUtilities.js";
export {
    MATH_UNITS,
    initializeUnitsWithMath,
} from "./utils/addMathUnits.js";
export * from "./Configurations/Units/UnitConfigurations.js";
export * from "./Configurations/Units/UnitPresetsConfigurations.js";
export * from "./Configurations/Units/UnitConversion.js";
export * from "./Configurations/Units/UnitLookup.js";

export * as SDI12SensorClusterConfig from "./Configurations/SDI12SensorClusterConfiguration.js";
export * as SensorConfig from "./Configurations/SensorConfigurations/SensorConfiguration.js";
export * as WarningConfig from "./Configurations/WarningConfigurations.js";
export { BASE_WARNING_VARIANT } from "./Configurations/WarningConfigurations.js";
export type {
    WarningFix,
    WarningFixKey,
    WarningProblem,
    WarningProblemKey,
    WarningVariant,
} from "./Configurations/WarningConfigurations.js";
export * as DeviceConfig from "./Configurations/DeviceConfiguration.js";
export * as ThirdPartyDeviceConfig from "./Configurations/ThirdPartyDeviceConfiguration.js";
export * as DataTypeConfig from "./Configurations/DataTypeConfigurations.js";
export {
    DeviceTypeConfigurations,
    MIN_GATEWAY_OFFLINE_THRESHOLD_MS,
} from "./Configurations/DeviceTypeConfiguration/DeviceTypeConfigurations.js";
export type {
    DeviceConfiguration,
    DeviceTypeConfiguration,
    ThirdPartyDeviceConfiguration,
} from "./Configurations/DeviceTypeConfiguration/types.js";

export { formatHoursAndMinutes, formatRoundedHoursOrMinutes } from "./durationFormat.js";
export { truncate, localeCollator } from "./textFormat.js";
export { normalizeText, TextFoldLevel } from "./utils/textUtils.js";

/** The device types a user can register to the dashboard. */
export type KnownDeviceType = FirstPartyDeviceType | ThirdPartyDeviceType;

type AllowableTypes = KnownDeviceType | SensorType;

type ConfigurationForType<T> = T extends FirstPartyDeviceType
    ? DeviceConfiguration
    : T extends ThirdPartyDeviceType
      ? ThirdPartyDeviceConfiguration
      : T extends SensorType
        ? SensorConfiguration
        : never;
export function getVerdiConfiguration<T extends AllowableTypes>(deviceType: T): ConfigurationForType<T> {
    return DeviceTypeConfigurations?.[deviceType.toString()] || SensorConfigurations[deviceType.toString()];
}

//These dictionaries must be empty. They ensure that we have not added the same string into two type defenitions.
//If Typescript is mad about this, it means you broke something. Go rename whatever you just created so it doesn't conflict.
//we are checking for any intersection between FirstPartyDeviceType, ThirdPartyDeviceType, SensorType, and DataTypeSpecifier.
//The two conflicts between SensorType and DataTypeSpecifier are being ignored for now, but should be removed at some point
//so that these types can all be considered explicit.

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const intersectionConfigs1: Record<FirstPartyDeviceType & ThirdPartyDeviceType, string> = {};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const intersectionConfigs2: Record<FirstPartyDeviceType & SensorType, string> = {};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const intersectionConfigs3: Record<SensorType & ThirdPartyDeviceType, string> = {};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const intersectionConfigs4: Record<ThirdPartyDeviceType & DataTypeSpecifier, string> = {};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const intersectionConfigs5: Record<SensorType & DataTypeSpecifier, string> = {
    v1flowmeter: "Fix eventually",
    v1transducer: "Fix eventually",
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const intersectionConfigs6: Record<FirstPartyDeviceType & DataTypeSpecifier, string> = {};

/**
 * Should be run to ensure all sensor configs are valid before we do anything further
 */
assertValidSensorConfigs();
